"use client";

import * as React from "react";

import { cn } from "@oneleet/utils";

import {
  AnimatePresence,
  TargetAndTransition,
  Transition,
  motion,
} from "framer-motion";
import { ChevronDown } from "lucide-react";

interface IconProps {
  className?: string;
}

// Create context for managing state
type AccordionContextValue = {
  activeItems: string | string[];
  setActiveItems: (value: string | string[]) => void;
  type: "single" | "multiple";
  toggle: (value: string) => void;
};

const AccordionContext = React.createContext<AccordionContextValue | null>(
  null,
);

// Custom implementation without relying on Radix UI
type AccordionSingleProps = {
  type: "single";
  collapsible?: boolean;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children?: React.ReactNode;
  width?: string | number;
};

type AccordionMultipleProps = {
  type: "multiple";
  defaultValue?: string[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  className?: string;
  children?: React.ReactNode;
  width?: string | number;
};

type AccordionItemProps = {
  value: string;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
};

type AccordionTriggerProps = {
  className?: string;
  startIcon?: React.ReactElement<IconProps>;
  endIcon?: React.ReactElement<IconProps>;
  children?: React.ReactNode;
};

type AccordionContentProps = {
  className?: string;
  animationProps?: {
    initial?: TargetAndTransition;
    animate?: TargetAndTransition;
    exit?: TargetAndTransition;
    transition?: Transition;
  };
  children?: React.ReactNode;
};

const Accordion = React.forwardRef<
  HTMLDivElement,
  AccordionSingleProps | AccordionMultipleProps
>(({ className, children, width, ...props }, ref) => {
  // Initialize state based on controlled or uncontrolled component
  const type = props.type;
  const isControlled = props.value !== undefined;

  // Set up initial state based on type
  const initialValue =
    type === "single"
      ? (props as AccordionSingleProps).defaultValue || ""
      : (props as AccordionMultipleProps).defaultValue || [];

  const [activeItems, setStateActiveItems] = React.useState<string | string[]>(
    initialValue,
  );

  // Use either controlled or uncontrolled state
  const currentValue = isControlled
    ? (props.value as string | string[])
    : activeItems;

  // Handle state changes and callbacks
  const setActiveItems = React.useCallback(
    (value: string | string[]) => {
      if (!isControlled) {
        setStateActiveItems(value);
      }

      if (type === "single") {
        const singleProps = props as AccordionSingleProps;
        singleProps.onValueChange?.(value as string);
      } else {
        const multipleProps = props as AccordionMultipleProps;
        multipleProps.onValueChange?.(value as string[]);
      }
    },
    [isControlled, props, type],
  );

  // Handle toggling items
  const toggle = React.useCallback(
    (value: string) => {
      if (type === "single") {
        const currentActiveValue = currentValue as string;
        const singleProps = props as AccordionSingleProps;
        // If collapsible is true, we can collapse the active item
        if (singleProps.collapsible && currentActiveValue === value) {
          setActiveItems("");
        } else {
          setActiveItems(value);
        }
      } else {
        const currentActiveValues = Array.isArray(currentValue)
          ? currentValue
          : [];
        if (currentActiveValues.includes(value)) {
          setActiveItems(currentActiveValues.filter((item) => item !== value));
        } else {
          setActiveItems([...currentActiveValues, value]);
        }
      }
    },
    [currentValue, props, setActiveItems, type],
  );

  // Provide context to children
  const contextValue = React.useMemo(
    () => ({
      activeItems: currentValue,
      setActiveItems,
      type,
      toggle,
    }),
    [currentValue, setActiveItems, type, toggle],
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      <div
        ref={ref}
        className={cn(
          "border-neutral-250 block rounded-lg border-[0.5px] bg-neutral-50",
          className,
        )}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement<AccordionItemProps>(child)) {
            return React.cloneElement(child, {
              ...child.props,
            });
          }
          return child;
        })}
      </div>
    </AccordionContext.Provider>
  );
});
Accordion.displayName = "Accordion";

// Hook to use the accordion context
const useAccordionContext = () => {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error(
      "Accordion components must be used within an Accordion component",
    );
  }
  return context;
};

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, children, value, disabled, ...props }, ref) => {
    const itemContext = React.useMemo(
      () => ({ value, disabled }),
      [value, disabled],
    );

    return (
      <AccordionItemContext.Provider value={itemContext}>
        <div
          ref={ref}
          className={cn(
            "border-neutral-250 box-border w-full border-b-[0.5px] last:border-b-0",
            className,
          )}
          data-value={value}
          {...props}
        >
          {children}
        </div>
      </AccordionItemContext.Provider>
    );
  },
);
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>(({ className, children, startIcon, endIcon, ...props }, ref) => {
  const { activeItems, toggle } = useAccordionContext();
  const item = React.useContext(AccordionItemContext);

  if (!item) {
    throw new Error("AccordionTrigger must be used within an AccordionItem");
  }

  const { value, disabled } = item;

  // Check if this item is active
  const isActive = Array.isArray(activeItems)
    ? activeItems.includes(value)
    : activeItems === value;

  return (
    <button
      ref={ref}
      className={cn(
        "text-heading-xs font-ng-medium flex w-full items-center justify-between px-4 py-3 transition-all [&[data-state=open]>svg:last-of-type]:rotate-180",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      onClick={() => toggle(value)}
      disabled={disabled}
      data-state={isActive ? "open" : "closed"}
      type="button"
      aria-expanded={isActive}
      {...props}
    >
      {startIcon ? startIcon : null}
      {children}
      {endIcon ? (
        endIcon
      ) : (
        <ChevronDown className="text-neutral-550 h-4 w-4 shrink-0 transition-transform duration-200" />
      )}
    </button>
  );
});
AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionContentProps
>(({ className, animationProps, children, ...props }, ref) => {
  const { activeItems } = useAccordionContext();
  const item = React.useContext(AccordionItemContext);

  if (!item) {
    throw new Error("AccordionContent must be used within an AccordionItem");
  }

  const { value } = item;

  // Check if this item is active
  const isActive = Array.isArray(activeItems)
    ? activeItems.includes(value)
    : activeItems === value;

  return (
    <AnimatePresence initial={false}>
      {isActive && (
        <motion.div
          initial={animationProps?.initial ?? { height: 0, opacity: 0 }}
          animate={animationProps?.animate ?? { height: "auto", opacity: 1 }}
          exit={animationProps?.exit ?? { height: 0, opacity: 0 }}
          transition={animationProps?.transition ?? { duration: 0.2 }}
          className="overflow-hidden"
        >
          <div
            ref={ref}
            className={cn("px-4 pb-4 pt-0", className)}
            data-state={isActive ? "open" : "closed"}
            {...props}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
AccordionContent.displayName = "AccordionContent";

// Item context to pass the value down to trigger and content
type AccordionItemContextType = {
  value: string;
  disabled?: boolean;
};

const AccordionItemContext =
  React.createContext<AccordionItemContextType | null>(null);

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  type AccordionSingleProps,
  type AccordionMultipleProps,
  type AccordionItemProps,
  type AccordionTriggerProps,
  type AccordionContentProps,
};
